/**
 * Traders Village San Antonio — published + industry benchmarks.
 *
 * Official (tradersvillage.com/san-antonio/vendors FAQ):
 *   "The market averages about 1,000 dealers."
 *   Open Saturday & Sunday only. Admission FREE. Parking $6.
 *
 * In-person vendor gross (industry surveys for flea markets):
 *   typically $200–$500 per vendor per open day → use $350 midpoint.
 *
 * Facebook / online projection (product rule):
 *   1 sale per vendor per calendar day (7 days/week).
 *   Pickup only on the 2 open market days → parking $6 per buyer at pickup.
 */

export const TVSA = {
  /** Official average from TVSA vendor FAQ — not a guess. */
  averageDealers: 1000,
  openDaysPerWeek: 2,
  /** Midpoint of common $200–$500/day flea-vendor gross benchmarks. */
  avgVendorGrossPerOpenDayUsd: 350,
  /** Typical ticket for a Facebook / app checkout line item. */
  avgOnlineSaleUsd: 28,
  parkingFeeUsd: 6,
  admissionFeeUsd: 0,
  sourceNote:
    'TVSA FAQ: averages ~1,000 dealers, Sat–Sun only, admission free, parking $6. In-person ~$350/vendor/open day (industry $200–$500).',
};

export function daysInMonth(year, monthIndex) {
  return new Date(year, monthIndex + 1, 0).getDate();
}

export function countWeekendDaysInMonth(year, monthIndex) {
  const nDays = daysInMonth(year, monthIndex);
  let n = 0;
  for (let d = 1; d <= nDays; d++) {
    const dow = new Date(year, monthIndex, d).getDay();
    if (dow === 0 || dow === 6) n += 1;
  }
  return n;
}

/**
 * Facebook / online: 1 sale / vendor / calendar day. Pickup on Sat–Sun only.
 * @param {'day'|'week'|'month'} period
 * @param {number} [vendorCount]
 */
export function buildFacebookProjection(period, vendorCount = TVSA.averageDealers) {
  const vendors = Math.max(1, vendorCount || TVSA.averageDealers);
  const now = new Date();
  const sellingDays =
    period === 'week'
      ? 7
      : period === 'month'
        ? daysInMonth(now.getFullYear(), now.getMonth())
        : 1;

  const pickupDays =
    period === 'week'
      ? TVSA.openDaysPerWeek
      : period === 'month'
        ? countWeekendDaysInMonth(now.getFullYear(), now.getMonth())
        : 1; // one pickup window tied to an open day

  const salesCount = vendors * sellingDays;
  const merchandiseGross = salesCount * TVSA.avgOnlineSaleUsd;
  // Each online buyer still visits once on an open day to pick up → $6 parking each
  const parkingBoost = salesCount * TVSA.parkingFeeUsd;
  const ownerMerchandiseCut = merchandiseGross * 0.02;

  return {
    period,
    vendors,
    sellingDays,
    pickupDays,
    salesCount,
    avgSaleUsd: TVSA.avgOnlineSaleUsd,
    merchandiseGross,
    parkingFeeUsd: TVSA.parkingFeeUsd,
    parkingBoost,
    admissionFeeUsd: TVSA.admissionFeeUsd,
    ownerMerchandiseCut,
    ownerTotalWithParking: ownerMerchandiseCut + parkingBoost,
    sourceNote: TVSA.sourceNote,
  };
}

/**
 * Expected in-person flea totals at TVSA scale (for UI copy / sanity checks).
 * day = 1 open day, week = Sat+Sun, month = weekend days in current month.
 */
export function buildInPersonBenchmark(period, vendorCount = TVSA.averageDealers) {
  const vendors = Math.max(1, vendorCount || TVSA.averageDealers);
  const now = new Date();
  const openDays =
    period === 'week'
      ? TVSA.openDaysPerWeek
      : period === 'month'
        ? countWeekendDaysInMonth(now.getFullYear(), now.getMonth())
        : 1;
  const merchandiseGross = vendors * TVSA.avgVendorGrossPerOpenDayUsd * openDays;
  return {
    period,
    vendors,
    openDays,
    merchandiseGross,
    ownerCut: merchandiseGross * 0.02,
    avgVendorGrossPerOpenDayUsd: TVSA.avgVendorGrossPerOpenDayUsd,
  };
}
